<template>
  <div class="w-full p-3 border rounded-lg flex space-x-4 text-left bg-white dark:bg-gray-950 dark:border-gray-800">
    <div class="h-12 w-12 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden flex items-center justify-center font-semibold">
      <img
        v-if="isImage"
        :src="preview"
        :alt="fileLabel"
        class="object-cover w-full h-full"
      >

      <span v-else class="text-blue-700 uppercase text-sm">{{ fileTypeLabel }}</span>
    </div>

    <div class="w-full min-w-0">
      <div class="flex items-center justify-between">
        <h5 class="min-w-0 pr-3 text-gray-700 dark:text-gray-200 truncate">
          {{ fileLabel }} <span class="text-gray-600 dark:text-gray-400 text-sm">- {{ sizeLabel }}</span>
        </h5>

        <div v-if="!errors && isValidSize" class="flex flex-shrink-0 items-center space-x-2">
          <div class="text-white leading-none text-sm p-[0.35rem] rounded" :class="progress === 100 ? 'bg-primary-600' : 'bg-blue-500'">
            <span>{{ progressLabel }}</span>
          </div>
          <o-button v-if="progress === 100" size="sm" flat icon="x" @click="handleRemove" />
        </div>

        <div v-if="errors || !isValidSize" class="flex flex-shrink-0 items-center space-x-2">
          <div v-if="errors" class="text-white leading-none text-sm p-[0.35rem] rounded bg-red-500">
            <span>{{ $t('general.error') }}</span>
          </div>

          <div v-if="!isValidSize" class="text-white leading-none text-sm p-[0.35rem] rounded bg-orange-500">
            <span>{{ $t('components.upload.elements.upload.tooLarge') }}</span>
          </div>

          <o-button v-if="errors" size="sm" flat icon="refresh" @click="handleRetry">
            {{ $t('components.upload.elements.upload.retry') }}
          </o-button>
          <o-button size="sm" flat icon="x" @click="handleRemove" />
        </div>
      </div>

      <div v-if="!errors" class="flex items-center space-x-5 mt-3">
        <div
          class="bg-gray-200 rounded-full h-2 w-full"
          role="progressbar"
          :aria-valuenow="progress"
          aria-valuemin="0"
          aria-valuemax="100"
        >
          <div
            class="relative overflow-hidden bg-blue-400 rounded h-2 text-center text-white text-sm font-medium transition-all flex items-center justify-center"
            :style="`width: ${progress}%; transition: width 2s;`"
            :class="{ 'shimmer' : progress !== 100 }"
          />
        </div>
      </div>

      <div v-if="errors">
        <template v-for="(error, errorIndex) in errors" :key="errorIndex">
          <ul class="text-sm text-red-600 font-medium mt-1">
            <li v-for="(item, itemIndex) in error" :key="`error-item-${itemIndex}`">
              {{ item }}
            </li>
          </ul>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useNuxtApp } from '#app'

const { $api, $auth, $i18n } = useNuxtApp()

interface Props {
  file: any
  params?: Record<string, any>
}

const props = withDefaults(defineProps<Props>(), {
  params: () => ({}),
})

const emit = defineEmits<{
  (e: 'update'): void
  (e: 'remove'): void
  (e: 'retry'): void
  (e: 'status', status: string): void
}>()

const progress = ref(0)
const errors = ref<any>(null)

const preview = computed(() => {
  return URL.createObjectURL(props.file)
})

const fileLabel = computed(() => {
  return props.file.name
})

const sizeLabel = computed(() => {
  const file = props.file

  return file.size > 1024
    ? file.size > 1048576
      ? Math.round(file.size / 1048576) + 'mb'
      : Math.round(file.size / 1024) + 'kb'
    : file.size + 'b'
})

const progressLabel = computed(() => {
  if (progress.value < 100) {
    return `${progress.value}%`
  }

  return $i18n.t('components.upload.elements.upload.complete')
})

const fileTypeLabel = computed(() => {
  const name = props.file.name
  const parts = name.split('.')

  return parts[parts.length - 1]
})

const isImage = computed(() => {
  return ['image/jpeg', 'image/png', 'image/gif'].includes(props.file.type)
})

const isValidSize = computed(() => {
  const limit = $auth.info?.plan?.uploadSizeLimit || 5000000
  return isImage.value || props.file.size <= limit
})

const isValidType = computed(() => {
  return true
})

onMounted(() => {
  if (isValidSize.value && isValidType.value) {
    uploadFile()
  } else {
    emit('status', 'invalid')
  }
})

async function uploadFile () {
  errors.value = null
  progress.value = 0
  emit('status', 'uploading')

  try {
    const file = props.file
    const caption = file.name
    const options = { onUploadProgress: handleUploadProgress }
    const params = {
      ...props.params,
      caption,
    }

    const apiResponse = await $api.upload.upload({ file, params }, options)
    const response = apiResponse?.data

    if (response) {
      progress.value = 100
      emit('status', 'complete')
      emit('update')
    }
  } catch (e: any) {
    errors.value = e.errors
    emit('status', 'error')
  }
}

function handleUploadProgress ($event: any) {
  progress.value = parseInt(String(Math.round(($event.loaded / $event.total) * 100)))
}

function handleRetry () {
  emit('retry')
}

function handleRemove () {
  emit('remove')
}
</script>



<style scoped>
.shimmer::after {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  transform: translateX(-100%);
  background-image: linear-gradient(
    90deg,
    rgba(233, 233, 233, 1) 0,
    rgba(233, 233, 233, 0.9) 50%,
    rgba(233, 233, 233, 0.8) 100%
  );
  animation: shimmer 2s ease-out infinite;
  content: "";
}

@keyframes shimmer {
  100% {
    transform: translateX(0%);
    opacity: 0;
  }
}

</style>
